import { HTMLAttributes, useCallback, useEffect, useState } from 'react'
import { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import { map } from 'lodash'

import BorderedCard from '@/components/base/bordered-card'
import Button from '@/components/base/button-more'
import CarouselDots from '@/components/base/carousel-dots'
import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'

import { cn } from '@/lib/utils'
import { WPImage, WPLink } from '@/types'

export type ShowcaseProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    pretitle: string
    title: string
    link: WPLink
    collection: {
      title: { rendered: string }
      featured_image: WPImage
      link: string
    }[]
  }>

const Showcase = ({ pretitle, title, link, collection, ...props }: ShowcaseProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [emblaRef, emblaApi] = useEmblaCarousel({
    active: true,
    breakpoints: {
      '(min-width: 768px)': { active: false }
    }
  })

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [])

  useEffect(() => {
    if (!emblaApi) return
    onSelect(emblaApi)
    emblaApi.on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onSelect])

  return (
    <Section {...props}>
      <Container>
        <Grid className="gap-y-[3rem] md:gap-y-[5rem]">
          <Grid className="col-span-2 gap-y-[3rem] md:col-span-6 lg:col-span-12">
            <div className={cn('col-span-2 md:col-span-3', 'grid gap-y-[3rem]')}>
              {pretitle ? (
                <Typography variant="upper-small" component="h3" className="font-bold uppercase">
                  {pretitle}
                </Typography>
              ) : null}

              {title ? (
                <Typography variant="main-title-default" component="h2">
                  {title}
                </Typography>
              ) : null}
            </div>

            <div
              className={cn(
                'col-span-2 md:col-span-3 lg:col-span-9',
                'flex md:items-end md:justify-end'
              )}
            >
              {link && link.url ? (
                <div className="flex">
                  <Button href={link?.url} target={link?.target}>
                    {link?.title}
                  </Button>
                </div>
              ) : null}
            </div>
          </Grid>

          <div
            ref={emblaRef}
            className={cn(
              'col-span-2 md:col-span-6 lg:col-span-12',
              '-mx-margin overflow-hidden px-margin'
            )}
          >
            <div
              className={cn(
                '-ml-margin flex touch-pan-y',
                'md:ml-0 md:grid md:grid-cols-6 md:items-start md:gap-x-gutter lg:grid-cols-12'
              )}
            >
              {map(collection, ({ title, featured_image, link }, index) => (
                <BorderedCard
                  key={`card-${index}`}
                  image={featured_image}
                  title={title?.rendered}
                  link={link}
                  className={cn(
                    'relative min-w-0 shrink-0 grow-0 basis-full pl-margin',
                    'md:col-span-2 md:pl-0 lg:col-span-4'
                  )}
                />
              ))}
            </div>

            <CarouselDots
              count={collection?.length}
              selectedIndex={selectedIndex}
              className="mt-[3rem] md:hidden"
            />
          </div>
        </Grid>
      </Container>
    </Section>
  )
}

export default Showcase
